import { useEffect, useRef, useState, useCallback } from 'react'
import type { Tweet, AlertNotification } from '../types'
import { useNotifications } from './useNotifications'

interface UseSSEOptions {
  onTweet?: (tweet: Tweet) => void
  onAlert?: (alert: AlertNotification) => void
  enabled?: boolean
}

interface UseSSEReturn {
  isConnected: boolean
  lastTweetId: number
  reconnect: () => void
}

export function useSSE(options: UseSSEOptions = {}): UseSSEReturn {
  const { onTweet, onAlert, enabled = true } = options
  const [isConnected, setIsConnected] = useState(false)
  const [lastTweetId, setLastTweetId] = useState(0)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttempts = useRef(0)
  const { showNotification, playSound } = useNotifications()

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
    }

    const url = `/api/stream?lastSeenId=${lastTweetId}`
    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onopen = () => {
      setIsConnected(true)
      reconnectAttempts.current = 0
    }

    eventSource.onerror = () => {
      setIsConnected(false)
      eventSource.close()

      // Exponential backoff for reconnection
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000)
      reconnectAttempts.current++

      reconnectTimeoutRef.current = setTimeout(() => {
        if (enabled) {
          connect()
        }
      }, delay)
    }

    eventSource.addEventListener('tweet', (event) => {
      try {
        const tweet: Tweet = JSON.parse(event.data)
        setLastTweetId(tweet.id)
        onTweet?.(tweet)
      } catch (err) {
        console.error('Failed to parse tweet event:', err)
      }
    })

    eventSource.addEventListener('alert', (event) => {
      try {
        const alert: AlertNotification = JSON.parse(event.data)
        onAlert?.(alert)

        // Show notification
        showNotification(
          `Alert: ${alert.keywords.join(', ')}`,
          {
            body: alert.tweet.content.slice(0, 100),
            tag: `alert-${alert.tweet.tweet_id}`,
          }
        )

        // Play sound
        playSound()
      } catch (err) {
        console.error('Failed to parse alert event:', err)
      }
    })

    eventSource.addEventListener('connection', (event) => {
      try {
        const data = JSON.parse(event.data)
        setIsConnected(data.connected)
      } catch (err) {
        console.error('Failed to parse connection event:', err)
      }
    })

    eventSource.addEventListener('heartbeat', () => {
      // Connection is alive
    })
  }, [lastTweetId, enabled, onTweet, onAlert, showNotification, playSound])

  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    reconnectAttempts.current = 0
    connect()
  }, [connect])

  useEffect(() => {
    if (enabled) {
      connect()
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [enabled, connect])

  return { isConnected, lastTweetId, reconnect }
}
